import React from 'react';
import { cn } from '@core/utils/cn';
import { ActivityChart } from './ActivityChart';
import { TimeframeChart } from './TimeframeChart';
import { GpxMap } from './GpxMap';
import { ActivityList } from './ActivityList';
import type { DayActivityData } from '../utils/useActivityData';
import type { ActivityItem } from './ActivityList';
import {
    formatDayCardDate,
    hasDailySteps,
    hasGpxData,
    hasTimeframeData
} from '../utils/dayCard.utils';

interface DayCardProps {
    dayData: DayActivityData;
    isOnlyDay: boolean;
    onActivityClick?: (activity: ActivityItem) => void;
}

const hasAnyData = (data: DayActivityData['data']) =>
    hasDailySteps(data) || hasTimeframeData(data) || hasGpxData(data);

export function DayCard({ dayData, isOnlyDay, onActivityClick }: DayCardProps) {
    return (
        <div className={cn('shrink-0', isOnlyDay ? 'w-full' : 'w-[700px]')}>
            <div className="mb-4 border-b pb-4 text-center">
                <h4 className="text-base font-semibold tracking-tight">
                    {formatDayCardDate(dayData.date)}
                </h4>
            </div>

            {!dayData.error && !hasAnyData(dayData.data) && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                    No activity recorded on this day.
                </p>
            )}

            {!dayData.error && hasAnyData(dayData.data) && (
                <div className="flex flex-col gap-4">
                    {onActivityClick && (
                        <ActivityList
                            data={dayData.data}
                            onActivityClick={onActivityClick}
                        />
                    )}
                    {hasDailySteps(dayData.data) ? (
                        <ActivityChart
                            data={dayData.data}
                            isOnlyDay={isOnlyDay}
                        />
                    ) : null}
                    {hasTimeframeData(dayData.data) ? (
                        <TimeframeChart
                            data={dayData.data}
                            isOnlyDay={isOnlyDay}
                        />
                    ) : null}
                    {hasGpxData(dayData.data) && (
                        <GpxMap data={dayData.data} isOnlyDay={isOnlyDay} />
                    )}
                </div>
            )}
        </div>
    );
}
